export type TypeAlertUnit = 'minutes' | 'hours' | 'days' | 'weeks';

export type TypeAlertAdd = { ok: true; alerts: number[] } | { ok: false; reason: string };

export const ALERTS_MAX = 5;

export const ALERT_MINUTES_MAX = 129_600;

export const ALERT_DAY_HOUR = 9;

const DAY = 1440;

const WEEK = 10_080;

export const ALERT_UNIT_MINUTES: Record<TypeAlertUnit, number> = { minutes: 1, hours: 60, days: DAY, weeks: WEEK };

export const ALERT_UNIT_LABEL: Record<TypeAlertUnit, string> = { minutes: 'minuti', hours: 'ore', days: 'giorni', weeks: 'settimane' };

const UNIT_WORDS: Record<TypeAlertUnit, [string, string]> = {
    minutes: ['minuto', 'minuti'],
    hours: ['ora', 'ore'],
    days: ['giorno', 'giorni'],
    weeks: ['settimana', 'settimane'],
};

export const alertUnits = (allDay: boolean): TypeAlertUnit[] => (allDay ? ['days', 'weeks'] : ['minutes', 'hours', 'days', 'weeks']);

export const quickAlerts = (allDay: boolean): number[] => (allDay ? [0, DAY, 2 * DAY, WEEK] : [0, 10, 30, 60, DAY, 2 * DAY, WEEK]);

export function defaultAlerts(allDay: boolean, reminder: boolean): number[] {
    if (reminder) return [0];
    return allDay ? [] : [10];
}

export const alertMinutes = (amount: number, unit: TypeAlertUnit): number => amount * ALERT_UNIT_MINUTES[unit];

export function splitAlert(minutes: number): { amount: number; unit: TypeAlertUnit } {
    const units: TypeAlertUnit[] = ['weeks', 'days', 'hours'];
    const unit = units.find((entry) => minutes > 0 && minutes % ALERT_UNIT_MINUTES[entry] === 0) ?? 'minutes';
    return { amount: minutes / ALERT_UNIT_MINUTES[unit], unit };
}

const spoken = (minutes: number): string => {
    const { amount, unit } = splitAlert(minutes);
    return `${amount} ${UNIT_WORDS[unit][amount === 1 ? 0 : 1]}`;
};

export function alertLabel(minutes: number, allDay: boolean): string {
    if (!allDay) {
        if (minutes === 0) return 'All’ora dell’evento';
        return `${spoken(minutes)} prima`;
    }
    if (minutes === 0) return `La mattina alle ${ALERT_DAY_HOUR}`;
    if (minutes === DAY) return `Il giorno prima alle ${ALERT_DAY_HOUR}`;
    if (minutes % DAY === 0) return `${spoken(minutes)} prima alle ${ALERT_DAY_HOUR}`;
    return `${spoken(minutes)} prima delle ${ALERT_DAY_HOUR}`;
}

const valid = (minutes: unknown): minutes is number =>
    typeof minutes === 'number' && Number.isInteger(minutes) && minutes >= 0 && minutes <= ALERT_MINUTES_MAX;

export function normalizeAlerts(alerts: unknown[]): number[] {
    return [...new Set(alerts.filter(valid))].sort((a, b) => b - a).slice(0, ALERTS_MAX);
}

export function alertsFullReason(alerts: number[]): string | null {
    return alerts.length >= ALERTS_MAX ? `Al massimo ${ALERTS_MAX} avvisi per evento` : null;
}

export function addAlert(alerts: number[], minutes: number): TypeAlertAdd {
    const full = alertsFullReason(alerts);
    if (full) return { ok: false, reason: full };
    if (!Number.isInteger(minutes) || minutes < 0) return { ok: false, reason: 'Serve un numero intero, da 0 in su' };
    if (minutes > ALERT_MINUTES_MAX) return { ok: false, reason: 'Al massimo 90 giorni prima' };
    if (alerts.includes(minutes)) return { ok: false, reason: 'Questo avviso c’è già' };
    return { ok: true, alerts: normalizeAlerts([...alerts, minutes]) };
}

export const removeAlert = (alerts: number[], minutes: number): number[] => alerts.filter((entry) => entry !== minutes);

export function convertAlerts(alerts: number[], toAllDay: boolean): number[] {
    if (!toAllDay) return normalizeAlerts(alerts);
    return normalizeAlerts(alerts.filter((minutes) => minutes >= DAY).map((minutes) => Math.round(minutes / DAY) * DAY));
}

export const digitsOnly = (text: string): string => text.replace(/\D+/g, '').slice(0, 6);

export function icsTrigger(minutes: number, allDay: boolean): string {
    const offset = allDay ? ALERT_DAY_HOUR * 60 - minutes : -minutes;
    if (offset === 0) return 'PT0M';
    return offset > 0 ? `PT${offset}M` : `-PT${-offset}M`;
}
